import { ReactNode } from "react";

import { ChartsLabelMarkProps } from "@mui/x-charts/ChartsLabel";
import { AxisGroup } from "@mui/x-charts/internals";

export type TimeseriesChartProps<T extends { date: number }> = MultiConfigChartProps<T> | SingleConfigChartProps<T>;

type SharedChartProps<T extends { date: number }> = {
    chartId: string;
    data: T[];
    height?: number;
    margin?: {
        left: number;
        top: number;
        bottom: number;
        right: number;
    };
    loading?: boolean;
    hideLegend?: boolean;
    headerComponents?: ReactNode;
    enableProjection?: boolean;
    xAxis?: XAxisOverride;
    /** Force the visible x-domain. Pair with the `useTimeWindow` hook + `<TimeDomainSelect>`. */
    xDomain?: [number, number];
    renderTooltip?: (ctx: TooltipContext<T>) => ReactNode;
} & TimeDomainProps;

export type TooltipSeriesItem = {
    seriesId: string | undefined;
    label: string | null;
    color: string | undefined;
    value: number | null;
    formattedValue: string;
};

export type TooltipContext<T> = {
    axisValue: number | undefined;
    axisFormattedValue: string | undefined;
    dataPoint: T | undefined;
    series: TooltipSeriesItem[];
};

/**
 * Override the default time-based x-axis. Use `scaleType: "bam"` or `"point"`
 * for non-time data (e.g. categorical buckets keyed by index).
 */
export type XAxisOverride = {
    scaleOverride?: "band" | "time";
    groups?: AxisGroup[];
    valueFormatter?: (value: number) => string;
};

export type SeriesValueFormatterContext = { dataIndex: number };

export type MultiConfigChartProps<T extends { date: number }> = SharedChartProps<T> & {
    series?: undefined;
    axisFormat?: undefined;
    disableAxisConstraint?: undefined;
    configOptions: ConfigOption<T>[];
    defaultConfig?: number;
    references?: undefined;
};

export type SingleConfigChartProps<T extends { date: number }> = SharedChartProps<T> & {
    series: SeriesConfig<T>[];
    axisFormat: FormatType;
    disableAxisConstraint?: boolean;
    configOptions?: undefined;
    defaultConfig?: undefined;
    references?: ChartsReference[];
};

export type ConfigOption<T extends { date: number }> = {
    label: string;
    series: SeriesConfig<T>[];
    axisFormat: FormatType;
    disableAxisConstraint?: boolean;
    references?: ChartsReference[];
};

export type SeriesConfig<T extends { date: number }> = {
    dataKey: SeriesKey<T>;
    label: string;
    labelCondensed?: string;
    color?: string;
    dash?: boolean;
    fill?: boolean;
    deltaFill?: DeltaSeriesConfig<T>;
    /** Override the default tooltip value formatting (default uses axisFormat). */
    valueFormatter?: (value: number | null, context: SeriesValueFormatterContext) => string;
    hideLine?: boolean;
};

export enum FormatType {
    Percent = "Percent",
    Date = "Date",
    Usd = "Usd",
    Token = "Token",
    Hidden = "Hidden",
    Datetime = "Datetime"
}

type NumericKeys<T> = {
    [K in keyof T]: T[K] extends number | undefined ? K : never;
}[keyof T];

/** Any key of T that is numeric and not "date" */
type SeriesKey<T extends { date: number }> = Exclude<NumericKeys<T> & string, "date">;

export type DeltaSeriesConfig<T extends { date: number }> = {
    valueSeries: SeriesKey<T>;
    referenceSeries: SeriesKey<T>;
    valueFillColor: string;
    /**
     * Color for the inverted fill (where reference > value). Omit when only one direction
     * is meaningful — e.g. rewards APY can never be negative, so the area below the base
     * line should never be filled.
     */
    referenceFillColor?: string;
};

export type ChartsLabelProps = {
    markType: ChartsLabelMarkProps["type"];
    markColor: string | undefined;
    label: string | null;
    spacing?: number;
};

export type TimeDomainPeriod = { label: string; seconds: number };

export type TimeDomainProps = {
    period?: number;
    setPeriod?: (v: number) => void;
    periodOptions?: TimeDomainPeriod[];
};

export type ChartsReference = {
    date: number;
    type: "line" | "point";

    label?: string;
};
